import { useCallback, useEffect, useRef, useState } from 'react'
import { useConfig } from '../../providers/config'
import { throttle } from 'lodash'
import { useUserUniqueId } from '../useUserUniqueId'

/**
 * @param {object} options
 * @param {boolean} options.paused
 * @param {number} options.throttleTime
 * @returns {{ status: "idle" | "loading" | "ready" | "error", action: (...args: any[]) => void }}
 */
export const useClerkScript = ({ paused: _paused, throttleTime = 0 } = {}) => {
	const {
		clerk: { enabled, apiKey },
	} = useConfig()

	const paused = _paused ?? (!enabled || !apiKey)
	const [status, setStatus] = useState('idle')
	const scriptRef = useRef(null)

	const userUniqueId = useUserUniqueId()

	const loadScript = useCallback(
		() =>
			(function (w, d) {
				if (w.Clerk) {
					// prevent script from being loaded multiple times
					setStatus('ready')
					return
				}
				var e = d.createElement('script')
				e.type = 'text/javascript'
				e.async = true
				e.src = (d.location.protocol == 'https:' ? 'https' : 'http') + '://cdn.clerk.io/clerk.js'
				e.id = 'clerk-script' // NOTE: added id to script
				//NOTE - added onload and onerror events to set status
				e.onload = function () {
					setStatus('loading')
				}
				e.onerror = function () {
					setStatus('error')
				}
				scriptRef.current = e
				var s = d.getElementsByTagName('script')[0]
				s.parentNode.insertBefore(e, s)
				w.__clerk_q = w.__clerk_q || []
				w.Clerk =
					w.Clerk ||
					function () {
						w.__clerk_q.push(arguments)
					}
			})(window, document),
		[]
	)

	useEffect(() => {
		if (enabled && status === 'idle' && !paused) {
			loadScript()
		}
	}, [enabled, loadScript, paused, status])

	// this cannot be wrapped with useCallback or it will not work
	const action = throttle((...args) => {
		window.Clerk(...args)
	}, throttleTime)

	useEffect(() => {
		if (status === 'loading' && apiKey) {
			action('config', {
				key: apiKey,
				visitor: userUniqueId,
				collect_email: true,
				debug: {
					enabled: process.env.NODE_ENV === 'development',
					level: 'error',
				},
			})
			setStatus('ready')
		}
	}, [status, apiKey, action, userUniqueId])

	useEffect(() => {
		if (scriptRef.current) {
			scriptRef.current.setAttribute('data-status', status)
		}
	}, [status])

	return { status, action }
}
